// PATH: src/api/embedCatalog.ts — lecture du catalogue du programme marocain
// (tc_embed_be). Portage de tc_chat_ui (src/api/curriculum/subjects.ts et
// tracks.ts) : mêmes routes, mêmes filtres, mêmes normalisations — l'école
// voit exactement le catalogue que voient ses élèves.
//
// Lecture seule, depuis le navigateur, avec le cookie tc_session : ces GET
// sont ouverts à tout compte authentifié (taxonomie uniquement, jamais de
// contenu de document). Toutes les réponses sont des tableaux JSON nus.
import { useMemo } from "react";
import type { AxiosInstance } from "axios";
import { useApiClient } from "./apiClients";

const BASE = "/v1";
/** Code du programme marocain dans le catalogue (celui de tc_chat_ui). */
export const MOROCCAN_PROGRAM_CODE = "MA";

export interface EmbedProgram { id: string; code: string; name: string }
export interface EmbedStage { id: string; code: string; name: string; sortOrder: number }
export interface EmbedGrade { id: string; stageId: string; code: string; name: string; sortOrder: number }
export interface EmbedTrack { id: string; gradeId: string; code: string; name: string; sortOrder: number }
export interface EmbedSubject { id: string; code: string; name: string; sortOrder: number }
export interface EmbedOffering { subjectId: string; sortOrder: number }

type Raw = Record<string, unknown>;

const idString = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim() !== "") return value.trim();
  if (typeof value === "number") return String(value);
  return null;
};

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const order = (value: unknown): number => (typeof value === "number" ? value : 0);

/** Tableau nu attendu ; toute autre forme vaut liste vide (comme tc_chat_ui). */
const rows = (data: unknown): Raw[] =>
  Array.isArray(data) ? data.filter((x): x is Raw => !!x && typeof x === "object") : [];

const bySortThenCode = <T extends { sortOrder: number; code: string }>(a: T, b: T) =>
  a.sortOrder - b.sortOrder || a.code.localeCompare(b.code);

const toProgram = (r: Raw): EmbedProgram | null => {
  const id = idString(r.id), code = text(r.code), name = text(r.name);
  return id && code && name ? { id, code, name } : null;
};

const toStage = (r: Raw): EmbedStage | null => {
  const id = idString(r.id), code = text(r.code), name = text(r.name);
  return id && code && name ? { id, code, name, sortOrder: order(r.sort_order) } : null;
};

const toGrade = (r: Raw): EmbedGrade | null => {
  const id = idString(r.id), stageId = idString(r.stage_id ?? r.stageId);
  const code = text(r.code), name = text(r.name);
  return id && stageId && code && name ? { id, stageId, code, name, sortOrder: order(r.sort_order) } : null;
};

const toTrack = (r: Raw): EmbedTrack | null => {
  if (r.is_active === false) return null;
  const id = idString(r.id), gradeId = idString(r.grade_id ?? r.gradeId);
  const code = text(r.code), name = text(r.name);
  return id && gradeId && code && name ? { id, gradeId, code, name, sortOrder: order(r.sort_order) } : null;
};

const toSubject = (r: Raw): EmbedSubject | null => {
  const id = idString(r.id), code = text(r.code), name = text(r.name);
  return id && code && name ? { id, code, name, sortOrder: order(r.sort_order) } : null;
};

const toOffering = (r: Raw): EmbedOffering | null => {
  const subject = r.subject && typeof r.subject === "object" ? (r.subject as Raw) : null;
  const subjectId = idString(r.subject_id ?? r.subjectId ?? subject?.id);
  return subjectId ? { subjectId, sortOrder: order(r.sort_order) } : null;
};

const keep = <T,>(list: (T | null)[]): T[] => list.filter((x): x is T => x !== null);

const build = (http: AxiosInstance) => ({
  /**
   * Le filtre `code` du serveur est un ILIKE '%MA%' : seul le programme dont le
   * code vaut EXACTEMENT « MA » est retenu. null si le catalogue n'en a pas.
   */
  getMoroccanProgram: () =>
    http
      .get(`${BASE}/programs`, { params: { code: MOROCCAN_PROGRAM_CODE, active_only: true } })
      .then((r) => keep(rows(r.data).map(toProgram)).find(
        (p) => p.code.toUpperCase() === MOROCCAN_PROGRAM_CODE
      ) ?? null),
  // Sans `sort`, le serveur ne garantit aucun ordre : on le demande, et on
  // retrie côté client par sécurité.
  getStages: (programId: string) =>
    http
      .get(`${BASE}/stages`, { params: { program_id: programId, sort: "sort_order" } })
      .then((r) => keep(rows(r.data).map(toStage)).sort(bySortThenCode)),
  getGrades: (stageId: string) =>
    http
      .get(`${BASE}/grades`, { params: { stage_id: stageId, sort: "sort_order" } })
      .then((r) => keep(rows(r.data).map(toGrade)).sort(bySortThenCode)),
  /** Liste vide = niveau sans filière. */
  getTracks: (gradeId: string) =>
    http
      .get(`${BASE}/tracks`, { params: { grade_id: gradeId, active_only: true } })
      .then((r) => keep(rows(r.data).map(toTrack)).sort(bySortThenCode)),
  /** only_active : une matière fermée aux élèves n'est pas proposée. */
  getSubjects: (programId: string) =>
    http
      .get(`${BASE}/subjects`, { params: { program_id: programId, only_active: "true" } })
      .then((r) => keep(rows(r.data).map(toSubject)).sort(bySortThenCode)),
  getOfferings: (gradeId: string, trackId?: string | null) =>
    http
      .get(`${BASE}/offerings`, {
        params: { grade_id: gradeId, ...(trackId ? { track_id: trackId } : {}), sort: "sort_order" },
      })
      .then((r) => keep(rows(r.data).map(toOffering)).sort((a, b) => a.sortOrder - b.sortOrder)),
});

export type EmbedCatalogApi = ReturnType<typeof build>;

export const useEmbedCatalogApi = (): EmbedCatalogApi => {
  const http = useApiClient("embed");
  return useMemo(() => build(http), [http]);
};
